import React, { useEffect, useState } from 'react';
import placeholderImage from '../assets/placeholder.png';
import AdvertisementCard from '../components/advertisements/AdvertisementCard';
import AdvertisementFormModal from '../components/advertisements/AdvertisementFormModal';
import Alert from '../components/common/Alert';
import Pagination from '../components/common/Pagination';
import Header from '../components/layout/Header';
import SideMenu from '../components/layout/SideMenu';
import ViewToggle from '../components/products/ViewToggle';
import { useAuth } from '../contexts/AuthContext';
import {
  Advertisement,
  AdvertisementType,
  createAdvertisement,
  CreateAdvertisementPayload,
  deleteAdvertisement,
  getAllAdvertisements,
  updateAdvertisement,
  UpdateAdvertisementPayload
} from '../services/advertisementService';
import { ApiErrorResponse } from '../services/authService';
import { getCategoriesByStore, getStoresByCategory } from '../services/categoryService';
import { getProductsByStore } from '../services/productService';
import layoutStyles from '../styles/layout.module.css';
import cardGridStyles from '../styles/shared/CardGrid.module.css';
import tableStyles from '../styles/shared/DataTable.module.css';
import { ViewMode } from '../types/category';
import styles from './AdvertisementsPage.module.css';

const DEFAULT_LIMIT = 20;

const AdvertisementsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role?.role_name?.toLowerCase() === 'admin';

  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [categories, setCategories] = useState<Array<{ id: number; category_name: string }>>([]);
  const [storesForModal, setStoresForModal] = useState<Array<{ id: number; name: string }>>([]);
  const [productsForModal, setProductsForModal] = useState<Array<{ id: number; product_name: string }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error'; isVisible: boolean }>({
    message: '',
    type: 'success',
    isVisible: false
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAdvertisement, setEditingAdvertisement] = useState<Advertisement | null>(null);

  useEffect(() => {
    if (alert.isVisible) {
      const timer = setTimeout(() => {
        setAlert(prev => ({ ...prev, isVisible: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alert.isVisible]);

  const showAlert = (message: string, type: 'success' | 'error') => {
    setAlert({ message, type, isVisible: true });
  };

  const hideAlert = () => {
    setAlert(prev => ({ ...prev, isVisible: false }));
  };

  useEffect(() => {
    if (!isAdmin) return;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        hideAlert();

        const [adsResult, fetchedCategories] = await Promise.all([
          getAllAdvertisements({ page, limit, search: search || undefined, sortBy, sortOrder }),
          getCategoriesByStore(0).catch(() => [])
        ]);

        setAdvertisements(adsResult.items);
        setTotal(adsResult.total);
        setCategories(fetchedCategories.map(cat => ({ id: cat.id, category_name: cat.category_name || cat.name || '' })));
        setStoresForModal([]);
        setProductsForModal([]);
      } catch (err) {
        const apiError = err as ApiErrorResponse;
        showAlert(apiError.message || 'Failed to load advertisements.', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isAdmin, page, limit, search, sortBy, sortOrder]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit) || 1;

  const handleAddAdvertisement = () => {
    setEditingAdvertisement(null);
    setStoresForModal([]);
    setProductsForModal([]);
    setIsModalOpen(true);
    hideAlert();
  };

  const handleEditAdvertisement = async (advertisement: Advertisement) => {
    setEditingAdvertisement(advertisement);
    setStoresForModal([]);
    setProductsForModal([]);
    hideAlert();

    const [storesRes, productsRes] = await Promise.all([
      advertisement.category_id
        ? getStoresByCategory(advertisement.category_id).then((stores) =>
          stores.map((s) => ({ id: s.id, name: s.store_name }))
        ).catch(() => [])
        : Promise.resolve([]),
      advertisement.store_id
        ? getProductsByStore(advertisement.store_id).then((products) =>
          products.map((p) => ({ id: p.id, product_name: p.product_name || p.name || '' }))
        ).catch(() => [])
        : Promise.resolve([]),
    ]);

    let storesList = storesRes;
    const storeObj = advertisement.store as { name?: string; store_name?: string } | undefined;
    const storeName = storeObj?.name ?? storeObj?.store_name;
    if (advertisement.store_id && storeName) {
      const hasCurrent = storesList.some((s) => s.id === advertisement.store_id);
      if (!hasCurrent) {
        storesList = [{ id: advertisement.store_id, name: storeName }, ...storesList];
      }
    }

    let productsList = productsRes;
    const productObj = advertisement.product as { product_name?: string; name?: string } | undefined;
    const productName = productObj?.product_name ?? productObj?.name;
    if (advertisement.product_id && productName) {
      const hasCurrent = productsList.some((p) => p.id === advertisement.product_id);
      if (!hasCurrent) {
        productsList = [
          { id: advertisement.product_id, product_name: productName },
          ...productsList,
        ];
      }
    }

    setStoresForModal(storesList);
    setProductsForModal(productsList);
    setIsModalOpen(true);
  };

  const handleCategoryChange = async (categoryId: number | null) => {
    setStoresForModal([]);
    setProductsForModal([]);
    if (categoryId) {
      try {
        const stores = await getStoresByCategory(categoryId);
        setStoresForModal(stores.map(s => ({ id: s.id, name: s.store_name })));
      } catch {
        setStoresForModal([]);
      }
    }
  };

  const handleStoreChange = async (storeId: number | null) => {
    setProductsForModal([]);
    if (storeId) {
      try {
        const products = await getProductsByStore(storeId);
        setProductsForModal(products.map(p => ({ id: p.id, product_name: p.product_name || p.name || '' })));
      } catch {
        setProductsForModal([]);
      }
    }
  };

  const handleDeleteAdvertisement = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this advertisement?')) {
      hideAlert();
      try {
        await deleteAdvertisement(id);
        setAdvertisements((prev) => prev.filter(a => a.id !== id));
        setTotal((t) => Math.max(0, t - 1));
        showAlert('Advertisement deleted successfully!', 'success');
      } catch (err) {
        const apiError = err as ApiErrorResponse;
        showAlert(apiError.message || 'Failed to delete advertisement.', 'error');
      }
    }
  };

  const handleToggleActive = async (ad: Advertisement) => {
    hideAlert();
    try {
      const updated = await updateAdvertisement(ad.id, { is_active: !ad.is_active });
      setAdvertisements(advertisements.map(a => a.id === ad.id ? updated : a));
      showAlert(updated.is_active ? 'Advertisement enabled successfully.' : 'Advertisement disabled successfully.', 'success');
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      showAlert(apiError.message || 'Failed to update advertisement status.', 'error');
    }
  };

  const handleFormSubmit = async (formData: CreateAdvertisementPayload | UpdateAdvertisementPayload) => {
    hideAlert();
    setIsSubmitting(true);
    try {
      if (editingAdvertisement) {
        const updated = await updateAdvertisement(editingAdvertisement.id, formData as UpdateAdvertisementPayload);
        setAdvertisements(advertisements.map(a => a.id === editingAdvertisement.id ? updated : a));
        showAlert('Advertisement updated successfully!', 'success');
      } else {
        const created = await createAdvertisement(formData as CreateAdvertisementPayload);
        setAdvertisements([created, ...advertisements]);
        showAlert('Advertisement created successfully!', 'success');
      }
      setIsModalOpen(false);
      setEditingAdvertisement(null);
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      showAlert(apiError.message || (editingAdvertisement ? 'Failed to update advertisement.' : 'Failed to create advertisement.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeLabel = (type: AdvertisementType): string => {
    const labels: Record<AdvertisementType, string> = {
      [AdvertisementType.HOME_MAIN]: 'Home Main',
      [AdvertisementType.HOME_RECOMMENDATION]: 'Home Recommendation',
      [AdvertisementType.HOME_RECENT]: 'Home Recent',
      [AdvertisementType.CATEGORY_MAIN]: 'Category Main',
    };
    return labels[type] || type;
  };

  const renderTableView = () => (
    <table className={tableStyles.dataTable}>
      <thead>
        <tr>
          <th>Image</th>
          <th>Title</th>
          <th>Type</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {advertisements.map((ad) => (
          <tr key={ad.id}>
            <td className={tableStyles.imageCell}>
              {ad.image_url ? (
                <img
                  src={ad.image_url}
                  alt={ad.title}
                  className={tableStyles.tableImage}
                  onError={(e) => {
                    e.currentTarget.src = placeholderImage;
                  }}
                />
              ) : (
                <div className={tableStyles.tablePlaceholder}>📢</div>
              )}
            </td>
            <td>{ad.title}</td>
            <td>{getTypeLabel(ad.type)}</td>
            <td>
              <span className={ad.is_active ? styles.activeBadge : styles.inactiveBadge}>
                {ad.is_active ? 'Active' : 'Inactive'}
              </span>
            </td>
            <td>
              <button onClick={() => handleToggleActive(ad)} className={`${tableStyles.actionButton} ${ad.is_active ? tableStyles.disableButton : tableStyles.enableButton}`}>
                {ad.is_active ? 'Disable' : 'Enable'}
              </button>
              <button onClick={() => handleEditAdvertisement(ad)} className={`${tableStyles.actionButton} ${tableStyles.editButton}`}>Edit</button>
              <button onClick={() => handleDeleteAdvertisement(ad.id)} className={`${tableStyles.actionButton} ${tableStyles.deleteButton}`}>Delete</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderCardView = () => (
    <div className={cardGridStyles.cardsGrid}>
      {advertisements.map((ad) => (
        <AdvertisementCard
          key={ad.id}
          advertisement={ad}
          onEdit={handleEditAdvertisement}
          onDelete={handleDeleteAdvertisement}
          onToggleActive={handleToggleActive}
          getTypeLabel={getTypeLabel}
        />
      ))}
    </div>
  );

  if (!isAdmin) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <div className={styles.titleBar}>
              <h2 className={styles.title}>Advertisements</h2>
            </div>
            <p className={styles.errorMessage}>You need admin privileges to access this page.</p>
          </main>
        </div>
      </div>
    );
  }

  if (isLoading && advertisements.length === 0) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p>Loading advertisements...</p>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={layoutStyles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <SideMenu />
        <main className={styles.contentArea}>
          <Alert
            message={alert.message}
            type={alert.type}
            isVisible={alert.isVisible}
            onClose={hideAlert}
          />

          <div className={styles.titleBar}>
            <h2 className={styles.title}>Advertisements</h2>
            <div className={styles.controls}>
              <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
              <button onClick={handleAddAdvertisement} className={styles.addButton}>Add Advertisement</button>
            </div>
          </div>

          <div className={styles.toolbar}>
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Search advertisements..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={styles.searchInput}
              />
              <button type="submit" className={styles.searchButton}>Search</button>
            </form>
            <label className={styles.sortLabel}>
              Sort by:
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [by, order] = e.target.value.split('-') as [string, 'asc' | 'desc'];
                  setSortBy(by);
                  setSortOrder(order);
                  setPage(1);
                }}
                className={styles.sortSelect}
              >
                <option value="id-desc">ID (newest)</option>
                <option value="id-asc">ID (oldest)</option>
                <option value="title-asc">Title (A–Z)</option>
                <option value="title-desc">Title (Z–A)</option>
                <option value="type-asc">Type</option>
                <option value="is_active-desc">Status (active first)</option>
              </select>
            </label>
          </div>

          {advertisements.length === 0 && !isLoading && (
            <p>No advertisements found. Click "Add Advertisement" to create one.</p>
          )}

          {advertisements.length > 0 && (
            viewMode === 'table' ? renderTableView() : renderCardView()
          )}

          {total > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={(newLimit) => { setLimit(newLimit); setPage(1); }}
              limitOptions={[10, 20, 50]}
              showLimitSelector
              disabled={isLoading}
            />
          )}

          <AdvertisementFormModal
            isOpen={isModalOpen}
            onClose={() => { setIsModalOpen(false); setEditingAdvertisement(null); hideAlert(); }}
            onSubmit={handleFormSubmit}
            initialData={editingAdvertisement}
            isLoading={isSubmitting}
            categories={categories}
            stores={storesForModal}
            products={productsForModal}
            onCategoryChange={handleCategoryChange}
            onStoreChange={handleStoreChange}
          />
        </main>
      </div>
    </div>
  );
};

export default AdvertisementsPage;
